import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signature pad: drawn-only (finger/stylus/mouse) — a drawn e-signature with our audit trail.
export default function WaiverStep({ name, email, phone, source, refInfo, onSigned, busy = false, submitLabel = "Agree & continue", extra = {} }) {
  const [waiver, setWaiver] = useState(null);
  const [mediaConsent, setMediaConsent] = useState(true);
  const [pledge, setPledge] = useState(false);
  const [confirmUncheck, setConfirmUncheck] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [err, setErr] = useState("");
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    axios.get(`${API}/site/waiver`).then((r) => setWaiver(r.data)).catch(() => setWaiver({ version: 0, text: "Waiver unavailable — please try again." }));
  }, []);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#111827";
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: p.clientX - r.left, y: p.clientY - r.top };
    };
    const down = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasInk(true); e.preventDefault(); };
    const up = () => { drawing.current = false; };
    c.addEventListener("mousedown", down); c.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    c.addEventListener("touchstart", down, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); c.addEventListener("touchend", up);
    return () => {
      c.removeEventListener("mousedown", down); c.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      c.removeEventListener("touchstart", down); c.removeEventListener("touchmove", move); c.removeEventListener("touchend", up);
    };
  }, [waiver]);

  const clearInk = () => {
    const c = canvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const sign = async () => {
    setErr("");
    if (!pledge) { setErr("Please check the Content Protection Pledge box to continue."); return; }
    if (!hasInk) { setErr("Please draw your signature in the box below."); return; }
    const signature = canvasRef.current.toDataURL("image/png");
    const signature_type = "drawn";
    try {
      const { data } = await axios.post(`${API}/site/waiver/sign`, {
        name, email, phone, signature, signature_type,
        media_consent: mediaConsent, content_pledge: pledge, source, ref: refInfo, ...extra,
      });
      onSigned({ waiver_signature_id: data.id, waiver_version: data.version, media_consent: mediaConsent });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't record your signature — please try again.");
    }
  };

  if (!waiver) return <div className="py-8 text-center text-white/60 text-sm" data-testid="waiver-loading">Loading waiver…</div>;

  return (
    <div data-testid="waiver-step">
      <div className="text-sm font-bold uppercase tracking-wide text-[#FFE01A] mb-2">Training Waiver & Media Release</div>
      <div className="max-h-52 overflow-y-auto rounded-lg bg-white text-[#000000] text-xs leading-relaxed p-3 whitespace-pre-wrap border border-white/20" data-testid="waiver-text">
        {waiver.text}
      </div>
      <div className="mt-3 rounded-xl border-2 border-[#FFE01A] bg-[#FFE01A]/10 p-3" data-testid="content-pledge-box">
        <div className="text-xs font-bold uppercase tracking-wide text-[#FFE01A] mb-1.5">Content Protection Pledge — required</div>
        <label className="flex items-start gap-2 text-xs text-white/90 cursor-pointer select-none">
          <input type="checkbox" checked={pledge} onChange={(e) => setPledge(e.target.checked)}
            className="mt-0.5 accent-[#FFE01A]" data-testid="waiver-content-pledge" />
          <span>
            I will <b>never download, screen-record, copy, share, or re-post</b> any course video or material —
            they are for viewing inside the app only. I'll use what I learn <b>responsibly and lawfully</b>, and
            I won't teach or demonstrate the techniques to others. I understand videos carry my name and account
            ID, so any leaked copy traces back to me.
          </span>
        </label>
      </div>
      <label className="flex items-start gap-2 mt-3 text-xs text-white/85 cursor-pointer select-none">
        <input type="checkbox" checked={mediaConsent}
          onChange={(e) => { if (!e.target.checked) { setConfirmUncheck(true); } else { setMediaConsent(true); } }}
          className="mt-0.5 accent-[#FFE01A]" data-testid="waiver-media-consent" />
        <span><b>Media consent (optional):</b> I allow photos/video of me from sessions or events to be used in promotional materials. I can still train if I leave this unchecked.</span>
      </label>
      {confirmUncheck && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/60 p-4" data-testid="media-consent-confirm">
          <div className="w-full max-w-sm rounded-2xl bg-white text-[#000000] p-5 shadow-xl">
            <div className="font-bold text-sm mb-1.5">Turn off media consent?</div>
            <p className="text-xs leading-relaxed text-[#000000] mb-4">
              Are you sure? Photos help us promote the training — you can still train either way.
            </p>
            <div className="flex gap-2">
              <button type="button" autoFocus
                onClick={() => { setMediaConsent(true); setConfirmUncheck(false); }}
                className="flex-1 py-2 rounded-full font-display font-bold uppercase tracking-wide text-xs border-0 cursor-pointer"
                style={{ background: "#FFE01A", color: "#000000" }} data-testid="media-consent-keep-on">
                Keep it on
              </button>
              <button type="button"
                onClick={() => { setMediaConsent(false); setConfirmUncheck(false); }}
                className="flex-1 py-2 rounded-full text-xs border border-[#C4C8CE] bg-transparent text-[#000000] cursor-pointer"
                data-testid="media-consent-no-thanks">
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-white/70">Draw your signature below (finger, stylus, or mouse){name ? ` — ${name}` : ""}</span>
        </div>
        <div className="relative">
          <canvas ref={canvasRef} width={560} height={140} className="w-full h-[110px] rounded-lg bg-white touch-none cursor-crosshair" data-testid="waiver-signature-pad" />
          {hasInk && (
            <button type="button" onClick={clearInk} className="absolute top-1.5 right-1.5 text-[10px] px-2 py-0.5 rounded bg-black/70 text-white border-0 cursor-pointer" data-testid="waiver-signature-clear">Clear</button>
          )}
        </div>
      </div>
      {err && <div className="text-[#FF5100] text-xs mt-2" data-testid="waiver-error">{err}</div>}
      <button type="button" onClick={sign} disabled={busy}
        className="mt-3 w-full py-2.5 rounded-full font-display font-bold uppercase tracking-wide text-sm border-0 cursor-pointer disabled:opacity-60"
        style={{ background: "#FFE01A", color: "#000000" }} data-testid="waiver-agree-button">
        {busy ? "Sending…" : submitLabel}
      </button>
      <p className="text-[10px] text-white/50 mt-1.5">By signing you agree to the waiver above (v{waiver.version}). Your signature, date, and time are recorded.</p>
    </div>
  );
}
